import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import OnboardingStepper from "./OnboardingStepper";
import AccountTypeStep from "./steps/AccountTypeStep";
import PurposeStep from "./steps/PurposeStep";
import PropertyStep from "./steps/PropertyStep";
import InviteStep from "./steps/InviteStep";
import GetReadyStep from "./steps/GetReadyStep";
import PlanStep from "./steps/PlanStep";
import JoinPropertyStep from "./steps/JoinPropertyStep";
import PendingAccessStep from "./steps/PendingAccessStep";
import TrialOfferCard from "./TrialOfferCard";
import TrialSubscriptionModal from "./TrialSubscription/SubscriptionModal";
import WelcomeModalWrapper, { OnboardingGlobalStyle } from "./WelcomeModal.styles";
import { propertyService } from "@iso/services";
import { ACCOUNT_TYPE, ONBOARDING_PLAN, SETUP_VARIANT } from "@iso/constants/onboarding";
import logo from "@iso/assets/images/logo.png";

// The first step asks who the user is; the rest of the flow depends on it.
// Farms, contractors and ag services businesses all set up a property of their
// own (a business's is named after the company) and so share one flow; only
// someone joining an existing property skips straight to asking for access.
// `wide` steps drop the photo panel so their content can use the full width.
// Invite is last so that a free-plan user sees the Gold trial offer beside it.
const setupFlow = (variant) => [
  { key: "purpose" },
  { key: "initial_property", variant },
  { key: "get_ready", variant },
  { key: "plan", wide: true },
  { key: "invitation", variant },
];

const FLOWS = {
  [ACCOUNT_TYPE.FARM]: setupFlow(SETUP_VARIANT.FARM),
  [ACCOUNT_TYPE.AG_SERVICES]: setupFlow(SETUP_VARIANT.BUSINESS),
  [ACCOUNT_TYPE.CONTRACTOR]: setupFlow(SETUP_VARIANT.BUSINESS),
  [ACCOUNT_TYPE.EXISTING_PROPERTY]: [{ key: "join_property" }],
};

const stepsFor = (accountType) => [
  { key: "account_type" },
  // Until a type is chosen, show the farm flow's length (the most common path).
  ...(FLOWS[accountType] || FLOWS[ACCOUNT_TYPE.FARM]),
];

const WelcomeModal = (props) => {
  const history = useHistory();
  const contentRef = useRef(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [accountType, setAccountType] = useState("");
  const [purpose, setPurpose] = useState("");
  const [propertyId, setPropertyId] = useState(null);
  const [propertyValues, setPropertyValues] = useState({});
  const [invitedEmails, setInvitedEmails] = useState([]);
  const [setupRequested, setSetupRequested] = useState(false);
  const [plan, setPlan] = useState("");
  const [trialStarted, setTrialStarted] = useState(false);
  const [visibleTrialModal, setVisibleTrialModal] = useState(false);
  // null = not checked yet; [] = nothing pending.
  const [pendingAccess, setPendingAccess] = useState(null);
  const [showPending, setShowPending] = useState(false);

  const steps = stepsFor(accountType);
  const step = steps[stepIndex];
  // The 3-month Gold trial is offered only to users who chose the free plan.
  const showTrialOffer = !showPending && step.key === "invitation" && plan === ONBOARDING_PLAN.BRONZE;

  // Someone who has asked to join a property has no properties until it is
  // approved; show them where things stand rather than starting over.
  useEffect(() => {
    if (!props.visibleWelcomeModal || pendingAccess !== null) return;
    propertyService
      .getPendingAccess()
      .then((res) => {
        const pending = (res && res.pending) || [];
        setPendingAccess(pending);
        if (pending.length > 0) setShowPending(true);
      })
      .catch(() => setPendingAccess([]));
  }, [props.visibleWelcomeModal, pendingAccess]);

  // Move focus to the new step's heading so keyboard and screen-reader users
  // land at the top of each step.
  useEffect(() => {
    const heading = contentRef.current && contentRef.current.querySelector("h1");
    if (heading) heading.focus();
  }, [stepIndex, showPending]);

  const goNext = () => setStepIndex((index) => Math.min(index + 1, steps.length - 1));
  const goBack = () => setStepIndex((index) => Math.max(index - 1, 0));

  const cancelModal = () => {
    props.cancelWelcomeModal();
  };

  const finishTo = (path) => {
    cancelModal();
    history.push(path);
  };

  const renderStep = () => {
    switch (step.key) {
      case "account_type":
        return (
          <AccountTypeStep
            accountType={accountType}
            onNext={(value) => {
              setAccountType(value);
              goNext();
            }}
          />
        );
      case "purpose":
        return (
          <PurposeStep
            purpose={purpose}
            onNext={(value) => {
              setPurpose(value);
              goNext();
            }}
          />
        );
      case "initial_property":
        return (
          <PropertyStep
            variant={step.variant}
            purpose={purpose}
            propertyId={propertyId}
            initialValues={propertyValues}
            onSaved={(property, values) => {
              setPropertyId(property.id);
              setPropertyValues(values);
              props.getPropertyList && props.getPropertyList();
              goNext();
            }}
          />
        );
      case "invitation":
        return (
          <InviteStep
            variant={step.variant}
            propertyId={propertyId}
            invitedEmails={invitedEmails}
            onInvited={(email) => setInvitedEmails((emails) => [...emails, email])}
            onFinish={() => finishTo("/map")}
          >
            {/* Below the desktop breakpoint the side panel is hidden, so the
                offer sits under the form instead. */}
            {showTrialOffer && (
              <div className="ob-trial-inline">
                <TrialOfferCard
                  started={trialStarted}
                  onTry={() => setVisibleTrialModal(true)}
                />
              </div>
            )}
          </InviteStep>
        );
      case "get_ready":
        return (
          <GetReadyStep
            variant={step.variant}
            propertyId={propertyId}
            setupRequested={setupRequested}
            onSetupRequested={() => setSetupRequested(true)}
            onNext={goNext}
            onGoToMap={() => finishTo("/map")}
          />
        );
      case "plan":
        return (
          <PlanStep
            propertyId={propertyId}
            plan={plan}
            onChosen={(value) => {
              setPlan(value);
              goNext();
            }}
          />
        );
      case "join_property":
        return (
          <JoinPropertyStep
            onApproved={() => props.getPropertyList && props.getPropertyList()}
            onFinish={(path) => (path ? finishTo(path) : cancelModal())}
          />
        );
      default:
        return null;
    }
  };

  return (
    <WelcomeModalWrapper
      visible={props.visibleWelcomeModal}
      onCancel={cancelModal}
      width="100%"
      closable={false}
      maskClosable={false}
      destroyOnClose
      footer={null}
    >
      <OnboardingGlobalStyle />
      <div className={`ob-page ${step.wide ? "is-wide" : ""}`}>
        <div className="ob-main">
          <header className="ob-header">
            <img className="ob-logo" src={logo} alt="AirAgri" />
            <button type="button" className="ob-skip" onClick={cancelModal}>
              Skip setup
            </button>
          </header>
          <div className="ob-content" ref={contentRef}>
            {/* Wait for the pending-access check so a user with a pending
                request doesn't see the first question flash up first. */}
            {pendingAccess === null ? null : showPending ? (
              <PendingAccessStep
                pending={pendingAccess}
                onFinish={cancelModal}
                onSetUpSomethingElse={() => setShowPending(false)}
              />
            ) : (
              renderStep()
            )}
          </div>
          {pendingAccess !== null && !showPending && (
            <OnboardingStepper
              current={stepIndex}
              total={steps.length}
              onBack={stepIndex > 0 ? goBack : null}
            />
          )}
        </div>
        {!step.wide &&
          (showTrialOffer ? (
            <aside className="ob-aside ob-aside-offer">
              <img className="ob-aside-offer-logo" src={logo} alt="" />
              <TrialOfferCard
                started={trialStarted}
                onTry={() => setVisibleTrialModal(true)}
              />
            </aside>
          ) : (
            <aside className="ob-aside" aria-hidden="true">
              <img className="ob-aside-logo" src={logo} alt="" />
            </aside>
          ))}
      </div>
      {visibleTrialModal && (
        <TrialSubscriptionModal
          propertyId={propertyId}
          visibleSubscriptionModal
          cancelSubscriptionModal={() => setVisibleTrialModal(false)}
          onSaved={() => setTrialStarted(true)}
        />
      )}
    </WelcomeModalWrapper>
  );
};

export default WelcomeModal;
